"use client";

import { Slider as ArkSlider } from "@ark-ui/react/slider";
import { insetFocusRing } from "@decibeltrade/web-core/recipes";
import { forwardRef } from "react";
import { cx, type RecipeVariantProps, sva } from "styled-system/css";

import { clamp } from "@/utils/clamp";

import { type FormControlProps } from "./form-field";

export type SliderVariant = NonNullable<RecipeVariantProps<typeof sliderStyles>>["variant"];

export interface SliderProps extends Partial<FormControlProps> {
  /** The value of the slider. */
  value: number;
  /** A callback to be invoked when the value of the slider changes. */
  onValueChange?: (value: number) => void;
  /** A callback to be invoked when the slider value change is done. */
  onValueChangeEnd?: (value: number) => void;
  /** The minimum value of the slider. */
  min?: number;
  /** The maximum value of the slider. */
  max?: number;
  /** The step value of the slider. */
  step?: number;
  /** The values of the tick marks to show on the slider. */
  marks?: number[];
  /** The format function for the marks. */
  markFormatter?: (mark: number) => string;
  /** The style variant of the slider. */
  variant?: SliderVariant;
  /** Whether to allow clicking on the marks to change the value. */
  clickableMarks?: boolean;
  /**
   * The aria-label of the slider thumb. Useful for providing an accessible name for the slider
   * when not composing with `FormField` for a visible label.
   */
  ariaLabel?: string;
  /** Class name for applying additional styles to the wrapper element. */
  className?: string;
}

/** A form control component for selecting a numeric value within a range. */
export const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      id,
      "aria-describedby": describedBy,
      min = 0,
      max = 1,
      step = 0.01,
      marks = [],
      markFormatter,
      value,
      onValueChange,
      onValueChangeEnd,
      variant = "default",
      ariaLabel,
      className,
    },
    ref,
  ) => {
    const validatedMin = Number.isNaN(min) ? 0 : min;
    const validatedMax = Number.isNaN(max) ? 1 : max;
    const validatedStep = Number.isNaN(step) ? 0.1 : step;
    const validatedValue = Number.isNaN(value) ? validatedMin : value;

    const styles = sliderStyles({ variant });

    return (
      <ArkSlider.Root
        ref={ref}
        aria-label={ariaLabel ? [ariaLabel] : undefined}
        min={validatedMin}
        max={validatedMax}
        step={validatedStep}
        value={[clamp(validatedValue, validatedMin, validatedMax)]}
        onValueChange={(details) => {
          const newValue = details.value[0];
          if (newValue !== undefined) onValueChange?.(newValue);
        }}
        onValueChangeEnd={(details) => {
          const newValue = details.value[0];
          if (newValue !== undefined) onValueChangeEnd?.(newValue);
        }}
        thumbAlignment="center"
        className={cx(styles.root, className)}
      >
        <ArkSlider.Control className={styles.control}>
          <ArkSlider.Track className={styles.track}>
            <ArkSlider.Range className={styles.range} />
          </ArkSlider.Track>
          <ArkSlider.Thumb
            index={0}
            className={cx(insetFocusRing({ rounded: "full" }), "group", styles.thumbWrapper)}
          >
            <div className={styles.thumbHighlight} />
            <div className={styles.thumb} />
            <ArkSlider.HiddenInput id={id} aria-describedby={describedBy} />
          </ArkSlider.Thumb>
        </ArkSlider.Control>
        {marks.length > 0 && (
          <ArkSlider.MarkerGroup className={styles.markerGroup}>
            {marks.map((mark) => (
              <ArkSlider.Marker key={mark} value={mark}>
                <div className={styles.marker} onClick={() => onValueChange?.(mark)}>
                  {markFormatter ? markFormatter(mark) : mark.toLocaleString()}
                </div>
              </ArkSlider.Marker>
            ))}
          </ArkSlider.MarkerGroup>
        )}
      </ArkSlider.Root>
    );
  },
);
Slider.displayName = "Slider";

const sliderStyles = sva({
  slots: [
    "root",
    "control",
    "track",
    "range",
    "thumbWrapper",
    "thumbHighlight",
    "thumb",
    "markerGroup",
    "marker",
  ],
  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      w: "full",
      gap: "4",
    },
    control: {
      display: "flex",
      alignItems: "center",
      w: "full",
      py: "4",
      cursor: "pointer",
      "&[data-dragging]": { cursor: "grabbing" },
    },
    track: {
      w: "full",
      h: "2",
      rounded: "primary",
      overflow: "hidden",
      transitionPreset: "colors",
      maskImage: "radial-gradient(circle 1px, #000 100%, transparent)",
      maskSize: "4px 2px",
      maskRepeat: "repeat",
    },
    range: {
      h: "2",
      transitionPreset: "colors",
    },
    thumbWrapper: {
      position: "relative",
      cursor: "grab",
      "&[data-dragging]": { cursor: "grabbing" },
      _before: { zIndex: "[1]" }, // Make focus ring appear above the thumb highlight
    },
    thumb: {
      position: "absolute",
      inset: "0",
      h: "[12px]",
      w: "[12px]",
      rounded: "full",
      borderWidth: "200",
      borderStyle: "solid",
      boxShadow: "[0 0 0 2px {colors.background.primary} inset]",
      transform: "translate(3px, 3px)",
      transitionPreset: "colors",
    },
    thumbHighlight: {
      h: "[18px]",
      w: "[18px]",
      rounded: "full",
      opacity: 0.4,
      transform: "scale(0)",
      transitionPreset: "transform",
      ".group:is(:hover, [data-dragging]) &": {
        transform: "scale(1)",
      },
    },
    markerGroup: {
      w: "full",
      textStyle: "highlight.sm",
      color: "text.secondary",
    },
    marker: {
      position: "relative",
      cursor: "pointer",
      pointerEvents: "auto",
      _hover: {
        color: "text.highlight",
      },
    },
  },
  variants: {
    variant: {
      default: {
        track: { bg: "slider.track" },
        range: { bg: "slider.range" },
        thumb: { bg: "slider.range", borderColor: "slider.range" },
        thumbHighlight: { bg: "slider.range" },
      },
      danger: {
        track: { bg: "slider.track.danger" },
        range: { bg: "slider.range.danger" },
        thumb: { bg: "slider.range.danger", borderColor: "slider.range.danger" },
        thumbHighlight: { bg: "slider.range.danger" },
      },
    },
  },
});

// Below are utilities for creating a non-linear slider.
export interface SliderStop {
  pct: number;
  val: number;
}

export function valueFromSliderPercent(stops: SliderStop[], p: number) {
  p = Math.max(0, Math.min(100, p));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (!a || !b) {
      continue;
    }
    if (p >= a.pct && p <= b.pct) {
      const t = (p - a.pct) / (b.pct - a.pct);
      return a.val + t * (b.val - a.val);
    }
  }
  return stops[stops.length - 1]?.val ?? 1;
}

export function sliderPercentFromValue(stops: SliderStop[], v: number) {
  v = Math.max(1, Math.min(10, v));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (!a || !b) {
      continue;
    }
    if (v >= a.val && v <= b.val) {
      const t = (v - a.val) / (b.val - a.val);
      return a.pct + t * (b.pct - a.pct);
    }
  }
  return 100;
}
